import _ from 'lodash'
import { EN, REGION_AVAILABLE_LANGUAGES } from 'src/i18n'
import Model from 'src/models/model/model'
import JsonObject from 'src/models/model/simple-types/JsonObject'
import { deleteSuffix } from 'src/router/utils'

export default class LocalizedJsonObject extends JsonObject {
  static name = 'LocalizedJsonObject'

  static parse (value, translatableKeys, ownerModel) {
    let parsedValue
    if (_.isString(value)) {
      parsedValue = JSON.parse(value)
    } else {
      parsedValue = value
    }

    if (!_.isObject(parsedValue) && !_.isArray(parsedValue)) {
      return parsedValue
    }

    const availableLanguages = Model.configuration?.currentCircuit?.availableLanguages || _.without(REGION_AVAILABLE_LANGUAGES, EN)
    _.each(availableLanguages, availableLanguage => {
      parsedValue = this.recursiveEnsureTranslationKeysForLanguage(parsedValue, translatableKeys, availableLanguage)
    })

    parsedValue = this.recursivelyProxyObjectsWithLocalizedAccess(parsedValue, translatableKeys, ownerModel)

    return parsedValue
  }

  /**
   * For nested JSON objects, our form fields don't work unless the objects are reactive. If keys are added after the object is made reactive,
   * those new keys are not reactive. This function is used to ensure that all possible translation keys are present in a JSON object
   * with translatable keys, so that they can be included in the reactivity.
   *
   * @param {Object} object
   * @param {string[]} translatableKeys
   * @param {string} languageLocale - e.g. 'es'
   * @returns {Object} - The given object with all possible translation keys present
   */
  static ensureTranslationKeysForLanguage (object, translatableKeys, languageLocale) {
    if (!languageLocale || languageLocale === EN || _.isEmpty(translatableKeys)) return object
    if (!_.isObject(object) || _.isFunction(object)) return object

    const presentEnglishBaseKeys = _(object).keys().intersection(translatableKeys).value()
    _.each(presentEnglishBaseKeys, englishBaseKey => {
      const localizedKey = `${englishBaseKey}_${languageLocale}`
      if (!_.has(object, localizedKey)) {
        object[localizedKey] = null
      }
    })
    return object
  }

  /**
   * Recursively ensures that all possible translation keys are present in a JSON object with translatable keys. See above.
   *
   * @param {Object} object
   * @param {string[]} translatableKeys
   * @param {string} languageLocale - e.g. 'es'
   */
  static recursiveEnsureTranslationKeysForLanguage (object, translatableKeys, languageLocale) {
    if (!languageLocale || languageLocale === EN || _.isEmpty(translatableKeys)) return object

    if (_.isArray(object)) {
      return _.map(object, item => LocalizedJsonObject.recursiveEnsureTranslationKeysForLanguage(item, translatableKeys, languageLocale))
    } else if (_.isObject(object) && !_.isFunction(object)) {
      _.forOwn(object, (value, key) => {
        if ((_.isObject(value) && !_.isFunction(value)) || _.isArray(value)) {
          object[key] = LocalizedJsonObject.recursiveEnsureTranslationKeysForLanguage(value, translatableKeys, languageLocale)
        }
      })
      return LocalizedJsonObject.ensureTranslationKeysForLanguage(object, translatableKeys, languageLocale)
    } else {
      return object
    }
  }

  /**
   * Returns a Proxy for an object that localizes translatable property access based on the model's
   * language. When accessing a translatable property on these proxies, the value returned will
   * actually be the value of the language-specific property name when the model's language is not English.
   * Non-English blank values fallback to English values.
   *
   * @param {any} object - The object to be proxied. Non-objects are returned as-is.
   * @param {string[]} translatables - e.g. ['name', 'description']
   * @param {Object} ownerModel - The Model instance whose object we are proxying.
   * @returns {any} - The proxied object or original non-object.
 */
  static proxyObjectWithLocalizedAccess (object, translatables, ownerModel) {
    if (!_.isObject(object) || _.isFunction(object)) return object
    if (!_.isArray(translatables) || _.isEmpty(translatables)) return object
    if (!_.isObject(ownerModel)) return object
    if (object.isProxy) return object

    const translatableKeysSet = new Set(translatables)
    // Model.js reactivity constantly accesses objects' keys repeatedly/redundantly MANY times
    // so it's important the proxy handler functions are performant. We do the simplest checks early.
    // The Reflect methods perform the normal, un-proxied behavior with the given arguments.
    return new Proxy(object, {
      get (target, propertyName, receiver) {
        if (propertyName === 'isProxy') {
          // This offers a way to check if the object we are interacting with is actually its proxy
          return true
        } else if (propertyName === 'ownerModel') {
          return ownerModel
        } else if (ownerModel.language === EN) {
          return Reflect.get(target, propertyName, receiver) // don't proxy
        } else if (ownerModel.isTranslationEditingAllowed && !ownerModel.previewing) {
          // After much pain I learned that bad things happen if we return a proxied/localized value to
          // watchers or form components that persist during ownerModel.language changes (e.g. Admin forms).
          // The wrong language value is watched so the wrong language data can end up written to the
          // wrong place. It's safer to just not proxy when the app is expected to edit translations.
          // But to allow <Preview> components to display other languages content in Admin, we use the
          // `previewing` model property as a way to still allow proxying in Admin.
          return Reflect.get(target, propertyName, receiver) // don't proxy
        } else if (!translatableKeysSet.has(propertyName)) {
          // Fall back to default getter behavior
          return Reflect.get(target, propertyName, receiver) // don't proxy
        }

        const [localizedKey, baseKey, wasLocalized] = LocalizedJsonObject.localizeKey(propertyName, ownerModel.language)
        if (!wasLocalized) {
          return Reflect.get(target, propertyName, receiver) // don't proxy
        } else if (translatableKeysSet.has(baseKey)) {
          const localizedValue = Reflect.get(target, localizedKey, receiver) // do proxy
          if (!LocalizedJsonObject.isBlankValue(localizedValue)) {
            return localizedValue
          } else {
            return Reflect.get(target, propertyName, receiver) // don't proxy
          }
        } else {
          // Accessing a localized key, e.g. "name_es", but "name" is not a translatable key.
          // Not allowed, return blank value.
          return null
        }
      }
      // Initially I also proxied `set` for the convenience of not having to suffix keys with `_es` (language)
      // when setting data. It does work, but after learning the painful reasons explained in the comments above,
      // I decided it's safer to just require the full localized key (e.g. "name_es") when setting data.
    })
  }

  static recursivelyProxyObjectsWithLocalizedAccess (object, translatables, ownerModel) {
    if (!_.isObject(object) || _.isFunction(object)) return object
    if (!_.isArray(translatables) || _.isEmpty(translatables)) return object
    if (!_.isObject(ownerModel)) return object
    if (object.isProxy) return object

    if (_.isArray(object)) {
      return _.map(object, item => LocalizedJsonObject.recursivelyProxyObjectsWithLocalizedAccess(item, translatables, ownerModel))
    } else if (_.isObject(object) && !_.isFunction(object)) {
      const proxiedObject = LocalizedJsonObject.proxyObjectWithLocalizedAccess(object, translatables, ownerModel)
      _.forOwn(proxiedObject, (value, key) => {
        if ((_.isObject(value) && !_.isFunction(value)) || _.isArray(value)) {
          proxiedObject[key] = LocalizedJsonObject.recursivelyProxyObjectsWithLocalizedAccess(value, translatables, ownerModel)
        }
      })
      return proxiedObject
    } else {
      return object
    }
  }

  /**
   * Naively appends the current language to the key if it's not already there.
   * This is intentionally not as robust as the API checks for performance reasons.
   * This should only be called for keys that are known to be translatable.
   *
   * @param {string} key
   * @param {string} currentLanguage - e.g. 'es'
   * @returns {[string, string, boolean]} - [localizedKey, baseKey, wasLocalized]
   */
  static localizeKey (key, currentLanguage) {
    let baseKey = key
    let wasLocalized = false
    if (!key || !_.isString(key) || currentLanguage === EN) {
      return [key, baseKey, wasLocalized]
    }

    const currentLanguageSuffix = `_${currentLanguage}`
    if (_.endsWith(key, currentLanguageSuffix)) {
      wasLocalized = true
      baseKey = deleteSuffix(key, currentLanguageSuffix)
      return [key, baseKey, wasLocalized]
    } else {
      wasLocalized = true
      return [`${key}_${currentLanguage}`, baseKey, wasLocalized]
    }
  }

  static isBlankValue (value) {
    return value !== false && (value === null || value === undefined || value === '')
  }
}
